import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { ColorPicker } from '@components/ColorPicker';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import TextEditor from '@components/TextEditor';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { GatedSwitch } from '@features/Paywall/GatedSwitch';
import { surveyService } from '@services';
import { useTranslation, useOpenUpgradeDrawer } from '@hooks';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { KB } from '@constants/file';
import { TwoColumnsForm } from './styles';

const FollowUpSelect = styled(Select)({
    width: '75px',
    '& .MuiFormControl-root': {
        padding: '0 !important',
    },
});

export const HalfFieldSelect = styled(Select)({
    maxWidth: 250,
    '@media (max-width: 600px)': {
        maxWidth: 'none',
    },
});

const followUpOptions = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
];

const Customization = ({ isView, selectedSurvey, isQrOnlyPlan = false }) => {
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = useState(false);

    const { control, setValue, getValues, watch } = useFormContext();
    const openUpgradeDrawer = useOpenUpgradeDrawer();

    const mainLocationId = watch('mainLocationId');
    const followUpEnable = watch('followUpEnable');
    const customizeQrCodeDesign = watch('customizeQrCodeDesign');
    const qrLogoUrl = watch('qrLogoUrl');
    const sendSurveyEnabled = watch('sendSurveyEnabled');
    const messageValue = watch('message');
    const followUpTextValue = watch('followUpText');

    const protectionOptions = [
        { value: 0, label: t('common.none') },
        { value: 1, label: t('FeedbackSurveys.day') },
        { value: 2, label: t('FeedbackSurveys.days3') },
        { value: 3, label: t('FeedbackSurveys.week') },
        { value: 4, label: t('FeedbackSurveys.weeks2') },
        { value: 5, label: t('FeedbackSurveys.month') },
        { value: 6, label: t('FeedbackSurveys.months3') },
        { value: 7, label: t('FeedbackSurveys.months6') },
        { value: 8, label: t('FeedbackSurveys.year') },
    ];

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await surveyService.uploadQRLogoImage(data, selectedSurvey.id);
            setValue('qrLogoUrl', response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error("Couldn't upload logo for survey");
        }
        setIsUploading(false);
        e.target.value = '';
    };

    const onDeleteLogo = () => setValue('qrLogoUrl', null, { shouldDirty: true });

    return (
        <>
            {/* Section: QR Code */}
            <Typography variant="subtitle1" sx={{ marginBottom: '16px', color: 'rgba(0,0,0,0.87)' }}>
                {t('FeedbackSurveys.qrCode')}
            </Typography>
            <Controller
                control={control}
                name="customizeQrCodeDesign"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => field.onChange(event.target.checked)}
                        label={t('FeedbackSurveys.customizeQrCodeDesign')}
                        sx={{ marginBottom: '8px' }}
                        disabled={isView}
                    />
                )}
            />
            {customizeQrCodeDesign ? (
                <>
                    <Typography variant="subtitle1">{t('common.logo')}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                        {t('common.imageUploadFormat', { size: '500KB' })}
                    </Typography>
                    <ImageUploaderSingle
                        maxSize={KB * 500}
                        minSize={10 * KB}
                        extensions="image/*"
                        cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                        logoUrl={qrLogoUrl}
                        disabled={isUploading || isView}
                        onDelete={onDeleteLogo}
                        uploadLogo={uploadLogo}
                        style={{ marginBottom: 24 }}
                        isUploading={isUploading}
                    />
                    <Controller
                        control={control}
                        name="topText"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{
                                    ...field,
                                    onChange: e => field.onChange(e.target.value),
                                }}
                                label={t('FeedbackSurveys.topText')}
                                fullWidth
                                error={!!error}
                                sx={{ marginBottom: '8px' }}
                                helperText={error ? t(error.message, { count: 42 }) : null}
                                disabled={isView}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="bottomText"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{
                                    ...field,
                                    onChange: e => field.onChange(e.target.value),
                                }}
                                label={t('FeedbackSurveys.bottomText')}
                                fullWidth
                                error={!!error}
                                sx={{ marginBottom: '8px' }}
                                helperText={error ? t(error.message, { count: 42 }) : null}
                                disabled={isView}
                            />
                        )}
                    />
                    <Typography variant="subtitle1" sx={{ marginBottom: '8px' }}>
                        {t('common.color_other')}
                    </Typography>
                    <TwoColumnsForm>
                        <Controller
                            control={control}
                            name="backgroundColor"
                            render={({ field, fieldState: { error }, ref }) => (
                                <ColorPicker
                                    ref={ref}
                                    inputProps={{
                                        ...field,
                                        onChange: e => {
                                            const val = e?.target?.value ? e.target.value : e;
                                            field.onChange(val);
                                        },
                                    }}
                                    label={t('common.background')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    disabled={isView}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="fillColor"
                            render={({ field, fieldState: { error }, ref }) => (
                                <ColorPicker
                                    ref={ref}
                                    inputProps={{
                                        ...field,
                                        onChange: e => {
                                            const val = e?.target?.value ? e.target.value : e;
                                            field.onChange(val);
                                        },
                                    }}
                                    label={t('FeedbackSurveys.fillColor')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    disabled={isView}
                                />
                            )}
                        />
                    </TwoColumnsForm>
                </>
            ) : null}

            {/* Section: Send Surveys */}
            <Typography variant="subtitle1" sx={{ marginTop: '24px', marginBottom: '16px', color: 'rgba(0,0,0,0.87)' }}>
                {t('FeedbackSurveys.enableSendSurveys')}
            </Typography>
            {isQrOnlyPlan ? (
                <GatedSwitch
                    gated
                    disabled={isView}
                    label={t('FeedbackSurveys.enableSendSurveys')}
                    onUpgradeClick={() =>
                        openUpgradeDrawer({
                            defaultPlan: BILLING_TYPE_MAP.PROFESSIONAL,
                            titleKey: 'Paywall.upgradeForSendSurveys',
                            subtitleKey: 'Paywall.upgradeForSendSurveysDescription',
                        })
                    }
                />
            ) : (
                <Controller
                    control={control}
                    name="sendSurveyEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={field.value !== false}
                            handleChange={event => {
                                setValue('sendSurveyEnabled', event.target.checked, { shouldDirty: true });
                            }}
                            label={t('FeedbackSurveys.enableSendSurveys')}
                            disabled={isView}
                        />
                    )}
                />
            )}
            <Typography
                variant="caption"
                sx={{
                    color: 'var(--text-secondary)',
                    marginBottom: isQrOnlyPlan || sendSurveyEnabled !== false ? '16px' : 0,
                }}
            >
                {t('FeedbackSurveys.enableSendSurveysDescription')}
            </Typography>
            {!isQrOnlyPlan && sendSurveyEnabled !== false && (
                <>
                    <Controller
                        control={control}
                        name="emailSubject"
                        render={({ field, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{
                                    ...field,
                                    onChange: e => field.onChange(e.target.value),
                                }}
                                label={t('FeedbackSurveys.emailSubject')}
                                fullWidth
                                sx={{ marginBottom: '16px' }}
                                disabled={isView}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="message"
                        render={({ field, fieldState: { error }, ref }) => (
                            <TextEditor
                                {...field}
                                ref={ref}
                                label={t('FeedbackSurveys.messageTemplate')}
                                initialValue={messageValue || ''}
                                onChange={text =>
                                    setValue('message', parseTipTapJSONToPlainText(text), {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    })
                                }
                                editable={!isView}
                                required
                                error={error}
                                placeholderContext={PLACEHOLDER_CONTEXT.SURVEY_TEMPLATE}
                                aiAssistant={{
                                    additionalOptions: [
                                        {
                                            text: t('AIAssistant.writeTemplate'),
                                            type: AITaskType.GenerateSurveyTemplateMessage,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            companyId: mainLocationId,
                                        },
                                    ],
                                }}
                                toolbar={<PostDynamicVariables disabled={isView} />}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="followUpEnable"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('followUpEnable')}
                                handleChange={event => {
                                    setValue('followUpEnable', event.target.checked, { shouldDirty: true });
                                }}
                                label={t('FeedbackSurveys.followUpLabel')}
                                disabled={isView}
                            />
                        )}
                    />
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', marginBottom: followUpEnable ? 0 : '24px' }}
                    >
                        {t('FeedbackSurveys.followUpDescription')}
                    </Typography>
                    {followUpEnable && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '16px 0' }}>
                                <Typography variant="body2">{t('FeedbackSurveys.wait')}</Typography>
                                <Controller
                                    control={control}
                                    name="followUpDelay"
                                    render={({ field, ref }) => (
                                        <FollowUpSelect
                                            {...field}
                                            ref={ref}
                                            label={t('common.delay')}
                                            options={followUpOptions}
                                            disabled={isView}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('FeedbackSurveys.days')}</Typography>
                            </div>
                            <Controller
                                control={control}
                                name="followUpText"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <TextEditor
                                        {...field}
                                        ref={ref}
                                        label={t('FeedbackSurveys.followUpMessage')}
                                        initialValue={followUpTextValue || ''}
                                        onChange={text =>
                                            setValue('followUpText', parseTipTapJSONToPlainText(text), {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            })
                                        }
                                        editable={!isView}
                                        required
                                        error={error}
                                        placeholderContext={PLACEHOLDER_CONTEXT.SURVEY_TEMPLATE}
                                        aiAssistant={{
                                            additionalOptions: [
                                                {
                                                    text: t('AIAssistant.writeTemplate'),
                                                    type: AITaskType.GenerateSurveyTemplateFollowUp,
                                                    taskType: AI_TASK_TYPE.CONTEXTUAL,
                                                    companyId: mainLocationId,
                                                },
                                            ],
                                        }}
                                        toolbar={<PostDynamicVariables disabled={isView} />}
                                    />
                                )}
                            />
                        </>
                    )}
                    <Controller
                        control={control}
                        name="overSurvey"
                        render={({ field, ref }) => (
                            <HalfFieldSelect
                                {...field}
                                ref={ref}
                                label={t('FeedbackSurveys.protectionLabel')}
                                options={protectionOptions}
                                disabled={isView}
                                helpTooltip={t('FeedbackSurveys.protectionTooltip')}
                            />
                        )}
                    />
                </>
            )}
        </>
    );
};

export default Customization;
